import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 📚 Wiki - Seção Acessos
 * 
 * Documentação completa sobre o sistema de acessos, grupos de permissões,
 * blocos modulares e gerenciamento de usuários. Voltada para produtores.
 */
const AccessosWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:shield-check" />
          <span>Sistema de Acessos</span>
        </div>
        <p className={styles.heroText}>
          Gerencie quem pode acessar o quê na sua conta. Controle total sobre permissões,
          grupos e usuários de forma simples e intuitiva.
        </p>
      </section>

      {/* Tipos de Usuários */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Tipos de Usuários</span>
          <p className={styles.sectionSubtitle}>
            Escolha o tipo certo para cada membro da sua equipe
          </p>
        </div>

        <Row className="g-4">
          <Col lg={4}>
            <div className={styles.userCard}>
              <div className={styles.cardIcon}>
                <Icon icon="mdi:account-tie" />
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardBadge}>Equipe Interna</div>
                <span className={styles.cardTitle}>Colaborador</span>
                <p>
                  Membros da sua equipe com acesso baseado em <strong>grupos de permissões</strong>.
                  Ideal para organizar sua equipe por função.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Acesso baseado em grupos</li>
                  <li><Icon icon="mdi:check-circle" /> Permissões personalizáveis</li>
                  <li><Icon icon="mdi:check-circle" /> Ideal para: Financeiro, Suporte</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:account-cash" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Vendas Exclusivas</div>
                <span className={styles.cardTitle}>Vendedor</span>
                <p>
                  Vendedores dedicados que trabalham exclusivamente para você.
                  Acesso automático a <strong>todos os seus produtos</strong>.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Vinculado a todos os produtos</li>
                  <li><Icon icon="mdi:check-circle" /> Acesso exclusivo</li>
                  <li><Icon icon="mdi:check-circle" /> Ideal para: Equipe de vendas</li>
                </ul>
              </div>
            </div>
          </Col>

          <Col lg={4}>
            <div className={styles.userCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Convidado por Você</div>
                <span className={styles.cardTitle}>Afiliado Direto</span>
                <p>
                  Afiliados que você convida diretamente. Você escolhe quais produtos
                  eles podem promover e define a comissão.
                </p>
                <ul className={styles.featureList}>
                  <li><Icon icon="mdi:check-circle" /> Produtos selecionados</li>
                  <li><Icon icon="mdi:check-circle" /> Comissão personalizada</li>
                  <li><Icon icon="mdi:check-circle" /> Aprovado automaticamente</li>
                </ul>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Grupos de Permissões */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Grupos de Permissões</span>
          <p className={styles.sectionSubtitle}>
            Organize sua equipe com grupos personalizados de permissões
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:lightbulb-on" className={styles.infoIcon} />
          <div>
            <span className={styles.cardTitle}>O que são Grupos de Permissões?</span>
            <p>
              Grupos são como "cargos" que você define para sua equipe. Cada grupo tem um conjunto
              de permissões que determina o que os colaboradores desse grupo podem fazer no sistema.
            </p>
            <p className={styles.tipText}>
              <strong>Dica:</strong> Crie grupos baseados em funções: "Financeiro", "Suporte", "Vendas".
              Você pode combinar múltiplos blocos de acesso em um mesmo grupo.
            </p>
          </div>
        </div>

        <div className={styles.stepsCard}>
          <span className={styles.cardTitle}>Como Criar um Grupo</span>
          <ol className={styles.stepsList}>
            <li>Vá em <strong>Acessos → Grupos de Permissões</strong></li>
            <li>Clique em <strong>"Novo Grupo"</strong></li>
            <li>Dê um nome ao grupo (ex: "Financeiro", "Administrador")</li>
            <li>Selecione os <strong>Blocos de Acesso</strong> desejados</li>
            <li>As permissões serão combinadas automaticamente</li>
            <li>Clique em <strong>"Salvar"</strong></li>
          </ol>
        </div>
      </section>

      {/* Blocos de Acesso */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Blocos de Acesso</span>
          <p className={styles.sectionSubtitle}>
            Templates pré-configurados que você pode combinar para criar perfis personalizados
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={`${styles.blockCard} ${styles.blockPrimary}`}>
              <div className={styles.blockHeader}>
                <Icon icon="mdi:office-building" />
                <span className={styles.cardTitle}>Gestão Interna</span>
              </div>
              <p className={styles.blockDescription}>
                Acesso completo à gestão do negócio. Ideal para equipe administrativa.
              </p>
              <div className={styles.permissionsList}>
                <span><Icon icon="mdi:view-dashboard" /> Dashboard</span>
                <span><Icon icon="mdi:package-variant" /> Produtos</span>
                <span><Icon icon="mdi:cart" /> Vendas</span>
                <span><Icon icon="mdi:account-network" /> Afiliados</span>
                <span><Icon icon="mdi:chart-box" /> Relatórios</span>
                <span><Icon icon="mdi:account-group" /> Acessos</span>
                <span><Icon icon="mdi:ticket-account" /> Tickets</span>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={`${styles.blockCard} ${styles.blockSuccess}`}>
              <div className={styles.blockHeader}>
                <Icon icon="mdi:currency-usd" />
                <span className={styles.cardTitle}>Cobrança e Financeiro</span>
              </div>
              <p className={styles.blockDescription}>
                Focado em gestão financeira, cobrança e controle de pagamentos.
              </p>
              <div className={styles.permissionsList}>
                <span><Icon icon="mdi:view-dashboard" /> Dashboard</span>
                <span><Icon icon="mdi:cart" /> Vendas</span>
                <span><Icon icon="mdi:credit-card-sync" /> AfterPay</span>
                <span><Icon icon="mdi:wallet" /> Saques</span>
                <span><Icon icon="mdi:account" /> Responsáveis</span>
                <span><Icon icon="mdi:chart-box" /> Relatórios</span>
                <span><Icon icon="mdi:ticket-account" /> Tickets</span>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={`${styles.blockCard} ${styles.blockInfo}`}>
              <div className={styles.blockHeader}>
                <Icon icon="mdi:clipboard-list" />
                <span className={styles.cardTitle}>Gestor de Pedidos</span>
              </div>
              <p className={styles.blockDescription}>
                Focado em acompanhamento e gestão de pedidos e vendas.
              </p>
              <div className={styles.permissionsList}>
                <span><Icon icon="mdi:view-dashboard" /> Dashboard</span>
                <span><Icon icon="mdi:cart" /> Vendas</span>
                <span><Icon icon="mdi:chart-box" /> Relatórios</span>
                <span><Icon icon="mdi:credit-card-sync" /> AfterPay</span>
                <span><Icon icon="mdi:ticket-account" /> Tickets</span>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={`${styles.blockCard} ${styles.blockWarning}`}>
              <div className={styles.blockHeader}>
                <Icon icon="mdi:headset" />
                <span className={styles.cardTitle}>Mentorado/Atendimento</span>
              </div>
              <p className={styles.blockDescription}>
                Focado em suporte ao cliente e atendimento.
              </p>
              <div className={styles.permissionsList}>
                <span><Icon icon="mdi:view-dashboard" /> Dashboard</span>
                <span><Icon icon="mdi:cart" /> Vendas (visualizar/editar)</span>
                <span><Icon icon="mdi:chart-box" /> Relatórios</span>
                <span><Icon icon="mdi:credit-card-sync" /> AfterPay</span>
                <span><Icon icon="mdi:ticket-account" /> Tickets</span>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Criar Usuários */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Criar Usuários</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para adicionar novos membros à sua equipe
          </p>
        </div>

        <Row className="g-4">
          <Col md={6}>
            <div className={styles.stepsCard}>
              <div className={styles.stepsIcon}>
                <Icon icon="mdi:account-tie" />
              </div>
              <span className={styles.cardTitle}>Colaborador</span>
              <ol className={styles.stepsList}>
                <li>Vá em <strong>Acessos → Usuários</strong></li>
                <li>Clique em <strong>"Novo Usuário"</strong></li>
                <li>Selecione: <strong>"Colaborador"</strong></li>
                <li>Preencha: Nome, Email, Telefone, Senha</li>
                <li>Selecione um <strong>Grupo de Permissões</strong></li>
                <li>Clique em <strong>"Salvar"</strong></li>
              </ol>
              <div className={styles.noteBox} style={{ marginTop: '12px' }}>
                <Icon icon="mdi:email" />
                <span>O usuário receberá um email com credenciais de acesso</span>
              </div>
            </div>
          </Col>

          <Col md={6}>
            <div className={styles.stepsCard}>
              <div className={`${styles.stepsIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:account-cash" />
              </div>
              <span className={styles.cardTitle}>Vendedor</span>
              <ol className={styles.stepsList}>
                <li>Vá em <strong>Acessos → Usuários</strong></li>
                <li>Clique em <strong>"Novo Usuário"</strong></li>
                <li>Selecione: <strong>"Vendedor"</strong></li>
                <li>Preencha: Nome, Email, Telefone, Senha</li>
                <li>Será vinculado a <strong>todos os produtos</strong></li>
                <li>Clique em <strong>"Salvar"</strong></li>
              </ol>
              <div className={styles.noteBox} style={{ marginTop: '12px' }}>
                <Icon icon="mdi:email" />
                <span>O usuário receberá um email com credenciais de acesso</span>
              </div>
            </div>
          </Col>

          <Col md={12}>
            <div className={styles.stepsCard}>
              <div className={`${styles.stepsIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:account-network" />
              </div>
              <span className={styles.cardTitle}>Afiliado Direto</span>
              <ol className={styles.stepsList}>
                <li>Vá em <strong>Acessos → Usuários</strong></li>
                <li>Clique em <strong>"Novo Usuário"</strong></li>
                <li>Selecione: <strong>"Afiliado Direto"</strong></li>
                <li>Preencha: Nome, Email, Telefone, Senha</li>
                <li>Selecione os <strong>produtos</strong> que ele pode promover</li>
                <li>Defina a <strong>comissão</strong> para cada produto</li>
                <li>Clique em <strong>"Salvar"</strong></li>
              </ol>
              <div className={styles.noteBox}>
                <Icon icon="mdi:email" />
                <span>O usuário receberá um email com credenciais de acesso. Ele pode usar essa senha para fazer login e, se desejar, alterá-la nas configurações do perfil.</span>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dicas e Boas Práticas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Aproveite ao máximo o sistema de acessos
          </p>
        </div>

        <Row className="g-4">
          <Col md={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:star" className={styles.tipIcon} />
              <span className={styles.cardTitle}>Criar Grupo "Administrador"</span>
              <p>
                Para dar acesso total, crie um grupo chamado <strong>"Administrador"</strong> e
                selecione <strong>todos os blocos</strong>. Qualquer colaborador nesse grupo terá
                acesso completo.
              </p>
            </div>
          </Col>

          <Col md={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:account-group" className={styles.tipIcon} />
              <span className={styles.cardTitle}>Organize por Função</span>
              <p>
                Crie grupos baseados nas funções da sua equipe: "Financeiro", "Suporte", "Vendas".
                Facilita o gerenciamento e você pode alterar permissões de vários colaboradores
                de uma vez.
              </p>
            </div>
          </Col>

          <Col md={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:shield-check" className={styles.tipIcon} />
              <span className={styles.cardTitle}>Princípio do Menor Privilégio</span>
              <p>
                Dê apenas as permissões necessárias para cada pessoa fazer seu trabalho. Você pode
                sempre adicionar mais permissões depois, mas é mais seguro começar com menos.
              </p>
            </div>
          </Col>

          <Col md={6}>
            <div className={styles.tipCard}>
              <Icon icon="mdi:refresh" className={styles.tipIcon} />
              <span className={styles.cardTitle}>Atualize Conforme Cresce</span>
              <p>
                Conforme sua equipe cresce ou as responsabilidades mudam, você pode facilmente
                ajustar as permissões editando o grupo ou movendo colaboradores entre grupos.
              </p>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Respostas rápidas para as dúvidas mais comuns
          </p>
        </div>

        <div className={styles.faqList}>
          {[
            {
              question: 'Posso combinar múltiplos blocos em um grupo?',
              answer: 'Sim! Você pode selecionar quantos blocos quiser em um grupo. As permissões serão combinadas automaticamente, dando ao colaborador acesso a todas as funcionalidades dos blocos selecionados.'
            },
            {
              question: 'Posso editar as permissões de um grupo depois de criá-lo?',
              answer: 'Sim! Você pode editar um grupo a qualquer momento. Todas as mudanças se aplicam automaticamente a todos os colaboradores que estão nesse grupo.'
            },
            {
              question: 'Qual a diferença entre Vendedor e Afiliado Direto?',
              answer: (
                <>
                  <strong>Vendedor:</strong> Trabalha exclusivamente para você, tem acesso a todos os seus produtos automaticamente e não pode se afiliar a outros produtores.
                  <br /><br />
                  <strong>Afiliado Direto:</strong> Você escolhe quais produtos ele pode promover, ele pode ter produtos de outros produtores também e você define a comissão por produto.
                </>
              )
            },
            {
              question: 'O que acontece quando um novo produto é criado?',
              answer: (
                <>
                  <strong>Vendedores:</strong> São automaticamente vinculados ao novo produto, mas com status "Pendente", exigindo sua aprovação.
                  <br /><br />
                  <strong>Afiliados Diretos:</strong> Precisam ser vinculados manualmente. Você pode fazer isso editando o afiliado ou criando uma nova afiliação.
                </>
              )
            },
            {
              question: 'O usuário precisa alterar a senha no primeiro login?',
              answer: (
                <>
                  <strong>Não!</strong> O usuário pode fazer login normalmente com a senha que recebeu por email. 
                  Se ele desejar alterar a senha, pode fazer isso a qualquer momento nas <strong>configurações do perfil</strong>, 
                  mas não é obrigatório. Isso torna o processo mais simples e direto.
                </>
              )
            }
          ].map((faq, index) => (
            <div key={index} className={styles.faqCard}>
              <span className={`${styles.cardTitle} ${styles.faqQuestion}`}>
                <Icon icon="mdi:help-circle" />
                {faq.question}
              </span>
              <p className={styles.faqAnswer}>
                {typeof faq.answer === 'string' ? faq.answer : faq.answer}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default AccessosWiki;
